import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import Button from '../Button';
import Modal from '../Modal';
import Skeleton from '../Skeleton/Skeleton';
import styles from './Spreadsheet.module.scss';

/* ────────────────────────────────────────────────────────────
 * Spreadsheet — frozen-column, sticky-header inline-edit grid.
 *
 * Spec: docs/redesign/SPREADSHEET_INLINE_EDIT.md §2.1 (API),
 * §3 (visual), §4 (keyboard), §5 (dirty state), §9 (loading /
 * empty), §10 (accessibility). Motion: MOTION.md §4.11
 * (cell save pulse) + §4.2 (save bar slide-in).
 *
 * This is PR A of the spec's §12 PR sequencing — paste handling
 * (§6, PR C) and the mobile card-view fallback (§8, PR D) are
 * deferred to follow-up PRs.
 *
 *   <Spreadsheet
 *     data={items}
 *     columns={columns}
 *     frozenColumns={1}
 *     onCellChange={(rowId, colId, value) => ...}
 *     onSave={(dirtyRecords) => api.save(dirtyRecords)}
 *     onDiscard={() => {}}
 *     loading={isLoading}
 *     emptyState={<EmptyState .../>}
 *   />
 *
 * `columns` shape:
 *   {
 *     id: 'small',                // required
 *     header: 'Small' | () => JSX,
 *     accessor: row => row.prices.small,
 *     editable: true,
 *     type: 'number' | 'text',
 *     align: 'left' | 'right' | 'center',
 *     width: 100,                 // preferred width (flex-basis)
 *     minWidth: 80,               // shrink floor — defaults to width
 *     flex: 2,                    // grow factor for spare space — defaults to 1
 *     validate: (v, row) => true | 'Error message',
 *     cell: ({ row, value, rowIndex }) => JSX (read-only render),
 *   }
 *
 * Internal state is owned here — consumers don't need to track
 * dirty cells, validation errors, or the save bar. On Save the
 * consumer calls its API and feeds fresh `data` back in; the
 * Spreadsheet reconciles dirty entries against the new values
 * and clears anything that committed successfully.
 * ──────────────────────────────────────────────────────────── */

const DEFAULT_COL_WIDTH = 120;

/* Owned editing input — Spreadsheet drives the editing lifecycle
 * so commit / cancel / blur all clear the parent's editing state
 * consistently. EditableCell from InlineEdit is for standalone
 * usage; here we need full external control. */
const SpreadsheetCellInput = ({
  initialValue,
  type,
  align,
  ariaLabel,
  onCommit,
  onCancel,
}) => {
  const [draft, setDraft] = useState(
    initialValue == null ? '' : String(initialValue)
  );
  const inputRef = useRef(null);

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.focus();
    // Select existing text so typing replaces it; when the editor
    // was opened by a printable character we want the caret at end.
    if (el.value && String(initialValue) === el.value) {
      try { el.select(); } catch (_) { /* number inputs throw */ }
    } else {
      const pos = el.value.length;
      try { el.setSelectionRange(pos, pos); } catch (_) { /* number inputs throw */ }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onKey = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onCommit(draft, e.shiftKey ? 'up' : 'down');
    } else if (e.key === 'Tab') {
      e.preventDefault();
      onCommit(draft, e.shiftKey ? 'left' : 'right');
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    }
  };

  const cls = [
    styles.cellInput,
    align === 'right' ? styles.alignRight : '',
    align === 'center' ? styles.alignCenter : '',
    type === 'number' ? styles.numeric : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <input
      ref={inputRef}
      className={cls}
      type={type === 'number' ? 'number' : 'text'}
      inputMode={type === 'number' ? 'decimal' : undefined}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onKeyDown={onKey}
      // Blur committing the current draft — Spreadsheet will clear
      // its editing state on the commit callback.
      onBlur={() => onCommit(draft, null)}
      aria-label={ariaLabel}
    />
  );
};

SpreadsheetCellInput.propTypes = {
  initialValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  type: PropTypes.string,
  align: PropTypes.string,
  ariaLabel: PropTypes.string,
  onCommit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

SpreadsheetCellInput.defaultProps = {
  initialValue: '',
  type: 'text',
  align: 'left',
  ariaLabel: '',
};

const Spreadsheet = ({
  data,
  columns,
  frozenColumns,
  getRowId,
  onCellChange,
  onSave,
  onDiscard,
  loading,
  saving,
  emptyState,
  saveLabel,
  discardLabel,
  className,
  ariaLabel,
}) => {
  // ── refs ─────────────────────────────────────────────────
  const gridRef = useRef(null);
  const cellRefs = useRef(new Map()); // `${ri}:${ci}` → DOM node
  const scrollWrapRef = useRef(null);

  // ── focus + edit state ───────────────────────────────────
  const [focused, setFocused] = useState(null);   // { ri, ci }
  const [editing, setEditing] = useState(null);   // { ri, ci, initial? }

  // ── dirty + validation + save-pulse state ────────────────
  const [dirty, setDirty] = useState(() => new Map()); // rowId → Map<colId, value>
  const [errors, setErrors] = useState(() => new Map()); // rowId → Map<colId, msg>
  const [savedPulse, setSavedPulse] = useState(() => new Map()); // rowId → Set<colId>

  const [discardOpen, setDiscardOpen] = useState(false);
  const [horizScrolled, setHorizScrolled] = useState(false);

  // ── helpers ──────────────────────────────────────────────
  const rowIdOf = useCallback(
    (row, ri) => (getRowId ? getRowId(row, ri) : row?.id ?? ri),
    [getRowId]
  );

  const dirtyCount = useMemo(() => {
    let n = 0;
    dirty.forEach((cols) => {
      n += cols.size;
    });
    return n;
  }, [dirty]);

  const errorCount = useMemo(() => {
    let n = 0;
    errors.forEach((cols) => {
      n += cols.size;
    });
    return n;
  }, [errors]);

  const baseValueOf = useCallback((row, col) => {
    if (col.accessor) return col.accessor(row);
    return row?.[col.id];
  }, []);

  const getCellValue = useCallback(
    (row, col, ri) => {
      const rid = rowIdOf(row, ri);
      const rowDirty = dirty.get(rid);
      if (rowDirty && rowDirty.has(col.id)) return rowDirty.get(col.id);
      return baseValueOf(row, col);
    },
    [dirty, rowIdOf, baseValueOf]
  );

  // ── reconcile dirty against fresh data (post-save) ──────
  // Spec §5: consumer is responsible for the API call; on success
  // they feed fresh data back in and we drop any dirty entry whose
  // base value now matches the dirty value (i.e. the save landed).
  // A successful drop also fires a save-success pulse on that cell.
  useEffect(() => {
    if (dirty.size === 0) return;
    let changed = false;
    const nextDirty = new Map();
    const justSaved = new Map();
    dirty.forEach((cols, rid) => {
      const rowIdx = data.findIndex((r, i) => rowIdOf(r, i) === rid);
      if (rowIdx === -1) {
        changed = true; // row gone — drop everything for it
        return;
      }
      const row = data[rowIdx];
      const stillDirty = new Map();
      cols.forEach((value, colId) => {
        const col = columns.find((c) => c.id === colId);
        const base = col ? baseValueOf(row, col) : row?.[colId];
        if (String(base) === String(value)) {
          changed = true;
          let set = justSaved.get(rid);
          if (!set) {
            set = new Set();
            justSaved.set(rid, set);
          }
          set.add(colId);
        } else {
          stillDirty.set(colId, value);
        }
      });
      if (stillDirty.size > 0) nextDirty.set(rid, stillDirty);
      else changed = true;
    });
    if (changed) {
      setDirty(nextDirty);
      if (justSaved.size > 0) {
        setSavedPulse(justSaved);
        const tid = setTimeout(() => setSavedPulse(new Map()), 600);
        return () => clearTimeout(tid);
      }
      // also clear stale errors for cells that no longer dirty
      setErrors((prev) => {
        if (prev.size === 0) return prev;
        const next = new Map();
        prev.forEach((cols, rid) => {
          const live = nextDirty.get(rid);
          const filtered = new Map();
          cols.forEach((msg, colId) => {
            if (live && live.has(colId)) filtered.set(colId, msg);
          });
          if (filtered.size > 0) next.set(rid, filtered);
        });
        return next;
      });
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, columns, rowIdOf, baseValueOf]);

  // ── beforeunload guard ───────────────────────────────────
  useEffect(() => {
    if (dirty.size === 0) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty.size]);

  // ── horizontal scroll detector (frozen column shadow) ────
  useEffect(() => {
    const el = scrollWrapRef.current;
    if (!el) return undefined;
    const onScroll = () => setHorizScrolled(el.scrollLeft > 0);
    onScroll();
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  // ── cell commit (dirty + validate) ───────────────────────
  const handleCellCommit = useCallback(
    (ri, col, newValue) => {
      const row = data[ri];
      const rid = rowIdOf(row, ri);
      const base = baseValueOf(row, col);
      const baseStr = base == null ? '' : String(base);
      const newStr = newValue == null ? '' : String(newValue);

      setDirty((prev) => {
        const next = new Map(prev);
        const cols = new Map(next.get(rid) ?? []);
        if (newStr === baseStr) cols.delete(col.id);
        else cols.set(col.id, newValue);
        if (cols.size === 0) next.delete(rid);
        else next.set(rid, cols);
        return next;
      });

      if (col.validate) {
        const result = col.validate(newValue, row);
        const msg = result === true || result == null ? '' : String(result);
        setErrors((prev) => {
          const next = new Map(prev);
          const cols = new Map(next.get(rid) ?? []);
          // Validation only applies to dirty cells — once a cell goes
          // clean it should not advertise a stale error.
          if (msg && newStr !== baseStr) cols.set(col.id, msg);
          else cols.delete(col.id);
          if (cols.size === 0) next.delete(rid);
          else next.set(rid, cols);
          return next;
        });
      }

      if (onCellChange) onCellChange(rid, col.id, newValue);
    },
    [data, rowIdOf, baseValueOf, onCellChange]
  );

  // ── focus helpers ────────────────────────────────────────
  const focusCell = useCallback((ri, ci) => {
    setFocused({ ri, ci });
    // Defer the imperative focus until after React's commit so the
    // destination cell has tabIndex=0 if we depend on it later.
    requestAnimationFrame(() => {
      const node = cellRefs.current.get(`${ri}:${ci}`);
      if (node) {
        node.focus({ preventScroll: false });
        node.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
    });
  }, []);

  const moveFocus = useCallback(
    (dir) => {
      if (!focused) return;
      const { ri, ci } = focused;
      const lastR = data.length - 1;
      const lastC = columns.length - 1;
      let nr = ri;
      let nc = ci;
      if (dir === 'up') nr = Math.max(0, ri - 1);
      else if (dir === 'down') nr = Math.min(lastR, ri + 1);
      else if (dir === 'left') nc = Math.max(0, ci - 1);
      else if (dir === 'right') nc = Math.min(lastC, ci + 1);
      if (nr === ri && nc === ci) return;
      focusCell(nr, nc);
    },
    [focused, data.length, columns.length, focusCell]
  );

  // Tab navigation skips non-editable (frozen) columns and wraps
  // across rows. Stops at the start/end of the grid.
  const moveFocusTab = useCallback(
    (shift) => {
      if (!focused) return;
      const totalRows = data.length;
      const totalCols = columns.length;
      const editableCols = columns
        .map((c, i) => (c.editable ? i : -1))
        .filter((i) => i >= 0);
      if (editableCols.length === 0) return;
      const { ri, ci } = focused;

      const editableIdx = editableCols.indexOf(ci);
      let nextEditableIdx;
      let nr = ri;
      if (shift) {
        nextEditableIdx = editableIdx === -1 ? editableCols.length - 1 : editableIdx - 1;
        if (nextEditableIdx < 0) {
          if (ri === 0) return;
          nr = ri - 1;
          nextEditableIdx = editableCols.length - 1;
        }
      } else {
        nextEditableIdx = editableIdx === -1 ? 0 : editableIdx + 1;
        if (nextEditableIdx >= editableCols.length) {
          if (ri === totalRows - 1) return;
          nr = ri + 1;
          nextEditableIdx = 0;
        }
      }
      focusCell(nr, editableCols[nextEditableIdx]);
    },
    [focused, data.length, columns, focusCell]
  );

  // ── save / discard ───────────────────────────────────────
  const triggerSave = useCallback(() => {
    if (errorCount > 0) {
      // scroll to first error
      const firstRid = errors.keys().next().value;
      const ri = data.findIndex((r, i) => rowIdOf(r, i) === firstRid);
      if (ri >= 0) {
        const errCols = errors.get(firstRid);
        const colId = errCols.keys().next().value;
        const ci = columns.findIndex((c) => c.id === colId);
        if (ci >= 0) focusCell(ri, ci);
      }
      return;
    }
    const records = [];
    dirty.forEach((cols, rid) => {
      const changes = {};
      cols.forEach((v, k) => {
        changes[k] = v;
      });
      records.push({ rowId: rid, changes });
    });
    if (onSave) onSave(records);
  }, [errorCount, errors, dirty, data, columns, rowIdOf, focusCell, onSave]);

  const confirmDiscard = useCallback(() => {
    setDiscardOpen(false);
    setDirty(new Map());
    setErrors(new Map());
    if (onDiscard) onDiscard();
  }, [onDiscard]);

  // ── grid-level keydown ───────────────────────────────────
  // Cell-internal editing has its own keydown handler on the input;
  // this fires only when the focused cell is in display mode.
  const onGridKeyDown = (e) => {
    if (editing) return;
    if (!focused) return;
    const { ri, ci } = focused;
    const col = columns[ci];

    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
      e.preventDefault();
      if (dirtyCount > 0) triggerSave();
      return;
    }

    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        setFocused(null);
        return;
      case 'ArrowUp':
        e.preventDefault();
        moveFocus('up');
        return;
      case 'ArrowDown':
        e.preventDefault();
        moveFocus('down');
        return;
      case 'ArrowLeft':
        e.preventDefault();
        moveFocus('left');
        return;
      case 'ArrowRight':
        e.preventDefault();
        moveFocus('right');
        return;
      case 'Tab':
        e.preventDefault();
        moveFocusTab(e.shiftKey);
        return;
      case 'Enter':
      case 'F2':
        if (col && col.editable) {
          e.preventDefault();
          setEditing({ ri, ci });
        }
        return;
      case 'Delete':
      case 'Backspace':
        if (col && col.editable) {
          e.preventDefault();
          handleCellCommit(ri, col, '');
        }
        return;
      default:
        break;
    }

    // Printable character → enter edit mode replacing cell value
    // (Excel/Sheets convention — power users rely on this).
    if (
      col &&
      col.editable &&
      !e.metaKey &&
      !e.ctrlKey &&
      !e.altKey &&
      e.key.length === 1
    ) {
      e.preventDefault();
      setEditing({ ri, ci, initial: e.key });
    }
  };

  // ── helpers for cell classes ─────────────────────────────
  const cellStateCls = (ri, ci, isDirty, hasErr) => {
    const isFocused = focused && focused.ri === ri && focused.ci === ci;
    const justSaved = savedPulse.get(rowIdOf(data[ri], ri))?.has(columns[ci].id);
    return [
      styles.cell,
      ci < frozenColumns ? styles.frozen : '',
      ci === frozenColumns - 1 ? styles.frozenEdge : '',
      ci === frozenColumns - 1 && horizScrolled ? styles.frozenScrolled : '',
      columns[ci].align === 'right' ? styles.alignRight : '',
      columns[ci].align === 'center' ? styles.alignCenter : '',
      columns[ci].type === 'number' ? styles.numeric : '',
      isFocused ? styles.cellFocused : '',
      isDirty ? styles.cellDirty : '',
      hasErr ? styles.cellErrored : '',
      justSaved ? styles.cellSaved : '',
    ]
      .filter(Boolean)
      .join(' ');
  };

  // Columns are flexible: `width` is the preferred size (flex-basis),
  // spare container space is distributed by `flex` grow factor, and
  // columns shrink down to `minWidth` before the wrap falls back to
  // horizontal scrolling. Header / body / skeleton rows get identical
  // flex inputs, so their computed widths always agree.
  const widthStyle = (col) => {
    const width = col.width || DEFAULT_COL_WIDTH;
    return {
      flex: `${col.flex ?? 1} 1 ${width}px`,
      minWidth: col.minWidth ?? width,
    };
  };

  // ── render ───────────────────────────────────────────────
  return (
    <div
      ref={gridRef}
      className={[styles.grid, className].filter(Boolean).join(' ')}
      role="grid"
      aria-label={ariaLabel}
      aria-rowcount={data.length + 1}
      aria-colcount={columns.length}
      onKeyDown={onGridKeyDown}
    >
      <div ref={scrollWrapRef} className={styles.scrollWrap}>
        {/* Header row */}
        <div className={styles.headerRow} role="row" aria-rowindex={1}>
          {columns.map((col, ci) => (
            <div
              key={col.id}
              className={[
                styles.headerCell,
                ci < frozenColumns ? styles.frozen : '',
                ci === frozenColumns - 1 ? styles.frozenEdge : '',
                ci === frozenColumns - 1 && horizScrolled ? styles.frozenScrolled : '',
                col.align === 'right' ? styles.alignRight : '',
                col.align === 'center' ? styles.alignCenter : '',
              ]
                .filter(Boolean)
                .join(' ')}
              role="columnheader"
              aria-colindex={ci + 1}
              style={widthStyle(col)}
            >
              {typeof col.header === 'function' ? col.header() : col.header}
            </div>
          ))}
        </div>

        {/* Body */}
        <div className={styles.body} role="rowgroup">
          {loading ? (
            <div className={styles.skeletonHolder} aria-busy="true">
              {Array.from({ length: 6 }).map((_, ri) => (
                <div key={ri} className={styles.skeletonRow}>
                  {columns.map((col, ci) => (
                    <div
                      key={col.id}
                      className={[
                        styles.cell,
                        styles.skeletonCell,
                        ci < frozenColumns ? styles.frozen : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      style={widthStyle(col)}
                    >
                      <Skeleton
                        width={ci === 0 ? '70%' : '55%'}
                        height="0.85rem"
                      />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ) : data.length === 0 ? (
            <div className={styles.emptyHolder}>{emptyState}</div>
          ) : (
            data.map((row, ri) => {
              const rid = rowIdOf(row, ri);
              const rowDirty = dirty.get(rid);
              const rowErr = errors.get(rid);
              return (
                <div
                  key={rid}
                  className={styles.row}
                  role="row"
                  aria-rowindex={ri + 2}
                >
                  {columns.map((col, ci) => {
                    const value = getCellValue(row, col, ri);
                    const isDirty = !!rowDirty?.has(col.id);
                    const errMsg = rowErr?.get(col.id);
                    const isFocused =
                      focused && focused.ri === ri && focused.ci === ci;
                    const isEditing =
                      editing && editing.ri === ri && editing.ci === ci;
                    const refCb = (node) => {
                      const key = `${ri}:${ci}`;
                      if (node) cellRefs.current.set(key, node);
                      else cellRefs.current.delete(key);
                    };

                    // Roving tabindex: exactly one cell is tabbable at
                    // any moment. Default to cell (0,0) when nothing is
                    // focused yet so Tab from outside the grid lands
                    // somewhere meaningful instead of skipping the grid
                    // entirely.
                    const isTabStop =
                      isFocused || (!focused && ri === 0 && ci === 0);

                    return (
                      <div
                        key={col.id}
                        ref={refCb}
                        id={`sheet-cell-${ri}-${ci}`}
                        className={cellStateCls(ri, ci, isDirty, !!errMsg)}
                        role="gridcell"
                        aria-colindex={ci + 1}
                        aria-readonly={col.editable ? 'false' : 'true'}
                        aria-invalid={errMsg ? 'true' : undefined}
                        aria-describedby={
                          errMsg ? `sheet-err-${ri}-${ci}` : undefined
                        }
                        tabIndex={isTabStop ? 0 : -1}
                        style={widthStyle(col)}
                        onClick={() => focusCell(ri, ci)}
                        onFocus={() => {
                          // Browser-driven focus (e.g. user Tabbed in
                          // from outside) — sync our internal state so
                          // the cell shows its selected outline.
                          if (!isFocused) setFocused({ ri, ci });
                        }}
                        onDoubleClick={() => {
                          if (col.editable) setEditing({ ri, ci });
                        }}
                      >
                        {col.editable && isEditing ? (
                          <SpreadsheetCellInput
                            key="edit"
                            initialValue={editing.initial ?? value ?? ''}
                            type={col.type || 'text'}
                            align={
                              col.align ||
                              (col.type === 'number' ? 'right' : 'left')
                            }
                            ariaLabel={
                              typeof col.header === 'string'
                                ? col.header
                                : col.id
                            }
                            onCommit={(v, nav) => {
                              handleCellCommit(ri, col, v);
                              setEditing(null);
                              // Enter → next/prev row (Excel convention).
                              // Tab → next/prev editable cell, wrapping
                              // across rows and skipping frozen columns
                              // (SPREADSHEET_INLINE_EDIT.md §4).
                              if (nav === 'down') moveFocus('down');
                              else if (nav === 'up') moveFocus('up');
                              else if (nav === 'right') moveFocusTab(false);
                              else if (nav === 'left') moveFocusTab(true);
                              // nav === null indicates a blur (user clicked
                              // somewhere outside the input) — don't restore
                              // focus to the cell or we'd steal focus from
                              // wherever the user just clicked.
                            }}
                            onCancel={() => {
                              setEditing(null);
                              focusCell(ri, ci);
                            }}
                          />
                        ) : (
                          <>
                            <span className={styles.cellValue}>
                              {col.cell
                                ? col.cell({ row, value, rowIndex: ri })
                                : value === '' || value == null
                                ? <span className={styles.placeholder}>—</span>
                                : value}
                            </span>
                            {isDirty && (
                              <span
                                className={styles.dirtyDot}
                                aria-hidden="true"
                              />
                            )}
                          </>
                        )}
                        {errMsg && (
                          <span
                            id={`sheet-err-${ri}-${ci}`}
                            className={styles.errorPopover}
                            role="tooltip"
                          >
                            {errMsg}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Save bar — slides up when any cell is dirty. */}
      {dirtyCount > 0 && (
        <div className={styles.saveBar} role="region" aria-label="Unsaved changes">
          <span className={styles.saveBarMessage}>
            <strong className={styles.saveBarCount}>
              {dirtyCount} unsaved change{dirtyCount === 1 ? '' : 's'}
            </strong>
            {errorCount > 0 && (
              <span className={styles.saveBarError}>
                {' · '}
                {errorCount} error{errorCount === 1 ? '' : 's'} — fix before saving
              </span>
            )}
          </span>
          <span className={styles.saveBarActions}>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setDiscardOpen(true)}
              disabled={saving}
            >
              {discardLabel}
            </Button>
            <Button
              variant="primary"
              size="sm"
              loading={saving}
              disabled={errorCount > 0}
              onClick={triggerSave}
            >
              {saveLabel}
            </Button>
          </span>
        </div>
      )}

      <Modal
        open={discardOpen}
        onClose={() => setDiscardOpen(false)}
        title="Discard unsaved changes?"
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setDiscardOpen(false)}>
              Keep editing
            </Button>
            <Button variant="danger" onClick={confirmDiscard}>
              Discard {dirtyCount} change{dirtyCount === 1 ? '' : 's'}
            </Button>
          </>
        }
      >
        <p style={{ margin: 0 }}>
          {dirtyCount} unsaved change{dirtyCount === 1 ? '' : 's'} will be lost.
          This cannot be undone.
        </p>
      </Modal>
    </div>
  );
};

Spreadsheet.propTypes = {
  data: PropTypes.array.isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      header: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
      accessor: PropTypes.func,
      editable: PropTypes.bool,
      type: PropTypes.string,
      align: PropTypes.oneOf(['left', 'right', 'center']),
      width: PropTypes.number,
      minWidth: PropTypes.number,
      flex: PropTypes.number,
      validate: PropTypes.func,
      cell: PropTypes.func,
    })
  ).isRequired,
  frozenColumns: PropTypes.number,
  getRowId: PropTypes.func,
  onCellChange: PropTypes.func,
  onSave: PropTypes.func,
  onDiscard: PropTypes.func,
  loading: PropTypes.bool,
  saving: PropTypes.bool,
  emptyState: PropTypes.node,
  saveLabel: PropTypes.string,
  discardLabel: PropTypes.string,
  className: PropTypes.string,
  ariaLabel: PropTypes.string,
};

Spreadsheet.defaultProps = {
  frozenColumns: 1,
  getRowId: null,
  onCellChange: null,
  onSave: null,
  onDiscard: null,
  loading: false,
  saving: false,
  emptyState: null,
  saveLabel: 'Save changes',
  discardLabel: 'Discard',
  className: '',
  ariaLabel: 'Spreadsheet',
};

export default Spreadsheet;
